/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react"

import { observeNotifier, type SseMessage } from "@pkg/sse-notifier/src"
import { useQueryClient } from "@tanstack/react-query"
import { useRecoilValue, useSetRecoilState } from "recoil"

import { invalidationIntents, queryKeys } from "~/api/cache"
import { useProposalFetcher } from "~/containers/Proposal/state/proposal/proposal-fetchers"
import { proposalRemoteState } from "~/containers/Proposal/state/proposal/proposal-state"
import {
  brochureSyncEpochState,
  syncWaitingState,
} from "~/containers/Proposal/state/sync-state"

const NotificationEvent: Record<string, string> = {
  DesignRequestAbandoned: `vision_design_request_abandoned`,
  DesignRequestCompleted: `vision_design_request_completed`,
  DesignRequestCreated: `vision_design_request_created`,
  DesignRequestEscalated: `vision_design_request_escalated`,
  DesignRequestUnpaused: `vision_design_request_unpaused`,
  EstimateUpdated: `vision_estimate_updated`,
  ImageryUpdated: `vision_imagery_updated`,
  QuoteUpdated: `vision_quote_updated`,
  SavingsUpdated: `vision_savings_updated`,
  IncentivesUpdated: `vision_incentives_updated`,
  ActiveLoanStatusUpdated: `vision_sungage_loan_status_updated`,
  ProposalDesignUpdated: `vision_proposal_design_updated`,
  DesignRequestRejected: `vision_design_request_rejected`,
  BrochureUpdated: `vision_brochure_updated`,
  ProposalUpdated: `vision_proposal_updated`,
  AuroraDesignSynchronizationUpdated: `vision_aurora_design_synchronization_updated`,
}

interface sseNotifierProps {
  channelNameSpec: string
  jwt?: string
  debounceSec?: number
}

type ServerEvent =
  | {
      type: `CONNECTED`
      messagesPossiblyMissed: boolean
    }
  | {
      type: `DISCONNECTED`
      reconnectDelaySec: number
      messagesPossiblyMissed: boolean
    }
  | {
      type: `MESSAGE`
      message: SseMessage
    }

export function useSSE(props: sseNotifierProps): void {
  const { channelNameSpec, debounceSec } = props
  const setSyncWaiting = useSetRecoilState(syncWaitingState)
  const setBrochureSyncEpoch = useSetRecoilState(brochureSyncEpochState)
  const { slug } = useRecoilValue(proposalRemoteState)
  const { refetch } = useProposalFetcher({ slug })
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!channelNameSpec) return
    const sub = observeNotifier({
      channelNameSpec: channelNameSpec,
      messageDebounceSeconds: debounceSec,
      sseUrl: `/notifier/stream`,
    }).subscribe({
      next: async (evt: ServerEvent) => {
        switch (evt.type) {
          case `MESSAGE`:
            console.debug(`SSE: ${evt.message.data}`)
            switch (evt.message.data) {
              case NotificationEvent.QuoteUpdated:
                setSyncWaiting((current) => ({ ...current, quotes: true }))
                break
              case NotificationEvent.ImageryUpdated:
                setSyncWaiting((current) => ({ ...current, imagery: true }))
                break
              case NotificationEvent.EstimateUpdated:
                setSyncWaiting((current) => ({ ...current, estimates: true }))
                break
              case NotificationEvent.SavingsUpdated:
                setSyncWaiting((current) => ({ ...current, savings: true }))
                await queryClient.invalidateQueries({
                  queryKey: queryKeys.proposal.byId(channelNameSpec),
                })
                break
              case NotificationEvent.IncentivesUpdated:
                setSyncWaiting((current) => ({ ...current, incentives: true }))
                await invalidationIntents.enfinTpoPricingInputsChanged(
                  queryClient,
                  channelNameSpec,
                )
                break
              case NotificationEvent.ProposalDesignUpdated:
                setSyncWaiting((current) => ({ ...current, design: true }))
                break
              case NotificationEvent.ActiveLoanStatusUpdated:
                setSyncWaiting((current) => ({
                  ...current,
                  activeLoan: true,
                }))
                break
              case NotificationEvent.BrochureUpdated:
                setSyncWaiting((current) => ({
                  ...current,
                  brochure: true,
                }))
                setBrochureSyncEpoch((current) => current + 1)
                await invalidationIntents.enfinTpoPricingInputsChanged(
                  queryClient,
                  channelNameSpec,
                )
                break
              case NotificationEvent.ProposalUpdated:
                console.count(`refetch proposal from SSE Notifier`)
                await refetch()
                await queryClient.invalidateQueries({
                  queryKey: queryKeys.proposal.byId(channelNameSpec),
                })
                break
              case NotificationEvent.AuroraDesignSynchronizationUpdated:
                setSyncWaiting((current) => ({ ...current, design: true }))
                await invalidationIntents.auroraDesignRefreshed(
                  queryClient,
                  channelNameSpec,
                )
                break
            }
            break
          case `CONNECTED`:
            console.debug(`SSE: connected`)
            setSyncWaiting({
              estimates: true,
              quotes: true,
              savings: true,
              imagery: true,
              incentives: true,
              activeLoan: true,
              design: true,
              brochure: true,
              proposal: false,
            })
            break
          case `DISCONNECTED`:
            console.debug(`SSE: disconnected`)
            break
          default:
            console.error(`Unknown SSE event type`, { evt })
        }
      },
    })

    return () => {
      sub?.unsubscribe()
    }
  }, [channelNameSpec, debounceSec, setSyncWaiting, queryClient])
}
